<template>
  <div :class="[classResolver(classes), { 'stat-tile--clickable': !!drillThrough }]" class="stat-tile" @click="handleClick">
    <div class="stat-tile__head">
      <span class="stat-tile__label">{{ label }}</span>
      <span v-if="icon" class="stat-tile__icon"><CygnusIcon :name="icon" /></span>
    </div>
    <div class="stat-tile__value">
      <CygnusIcon v-if="loading" name="spinner" spin />
      <span v-else-if="error" class="stat-tile__value--error">&mdash;</span>
      <span v-else>{{ formattedValue }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import classResolver from '@apexl/icarus-core/src/services/classResolver';
import useDataSource from '@root/hooks/useDataSource';
import CygnusIcon from '@apexl/icarus-core/src/components/icons/CygnusIcon.vue';

// Icarus counterpart of `apexl\Io\modules\dynamicComponents\Component\Tiles\StatTile`
// (registry NAME `StatTile`) — see that class's docblock for the hybrid
// PHP-shell/Vue-fetch pattern this implements.
const props = defineProps({
  ...commonProps,
  dataSource: {
    type: String,
    required: true,
  },
  dataSourceParams: {
    type: [Array, Object],
    default: () => ([]),
  },
  label: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  drillThrough: {
    type: String,
    default: null,
  },
  format: {
    type: String,
    default: '',
  },
  prefix: {
    type: String,
    default: '',
  },
  suffix: {
    type: String,
    default: '',
  },
  // eslint-disable-next-line vue/no-unused-properties -- consumed by the
  // data-source layer's cache TTL once that lands; accepted here so the
  // PHP-baked prop doesn't fall through as an unknown attribute.
  ttl: {
    type: Number,
    default: null,
  },
});

const router = useRouter();
const { value, loading, error } = useDataSource(props.dataSource, props.dataSourceParams);

const formattedValue = computed(() => {
  if (value.value === null || value.value === undefined || value.value === '') {
    return '—';
  }

  let formatted = value.value;

  if (typeof formatted === 'number') {
    if (props.format === 'compact') {
      formatted = new Intl.NumberFormat(undefined, { notation: 'compact' }).format(formatted);
    } else if (props.format === 'percent') {
      formatted = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 }).format(formatted / 100);
    } else if (props.format === 'currency') {
      formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(formatted);
    } else {
      formatted = new Intl.NumberFormat().format(formatted);
    }
  }

  return `${props.prefix}${formatted}${props.suffix}`;
});

function handleClick() {
  if (!props.drillThrough) {
    return;
  }

  if (props.drillThrough.match(/^https?:\/\//)) {
    window.location.href = props.drillThrough;
    return;
  }

  router.push(props.drillThrough);
}
</script>

<style lang="scss">
.stat-tile {
  display: block;
  height: 100%;
  padding: 14px 16px;
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
  border-radius: var(--theme-radius-lg, 10px);
  box-shadow: var(--theme-shadow-card);
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.15s ease;

  &--clickable {
    cursor: pointer;

    &:hover {
      box-shadow: var(--theme-shadow-panel);
    }
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-accent-light);
    color: var(--theme-accent-dark);
    font-size: 0.85rem;
  }

  &__label {
    color: var(--theme-text-secondary);
    font-size: 0.75rem;
    font-weight: 600;
  }

  &__value {
    color: var(--theme-text-primary);
    font-size: 1.25rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;

    &--error {
      color: var(--theme-text-tertiary);
    }
  }
}
</style>
